import { makeAutoObservable, runInAction } from "mobx";
import OrderService, {
    IFetchOrder,
    IOrderRmaHeader,
    IOrder,
    IProduct,
    PRODUCT_STATUS_ENUM,
    ISalesChannel, ILocation, IOutOfPolicyOrderResultDto,
    IOrderRmaList
} from '../../../services/order-service';
import currentPortalStore from "../../../stores/current-portal-store";
import calculateDaysPassed from "../helpers/calculateDaysPassed";
import CustomerStore from "./customer-store";
import customersMainStore from "./customers-main-store";
import OrderProductStore from "./order-product-store";
import { TYPE, rootContainer } from "../../../services/container/root";

class OrderStore {
	private _model: IOrder;
    private _eligibilityResponse?: IOutOfPolicyOrderResultDto;
	private _productList: OrderProductStore[] = [];
	private _rmaList: IOrderRmaHeader;
    private _rmasList?: IOrderRmaList;
    private _isLoadingRmas: boolean = false;

	constructor(
        data: IFetchOrder,
        eligibilityResponse?: IOutOfPolicyOrderResultDto,
    ) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._rmaList = data.rma_list;
        this._model = data.order;
        this._setProductStores(data.order.products);
		this._eligibilityResponse = eligibilityResponse;
	}

    private get _orderService(): OrderService { return rootContainer.get<OrderService>(TYPE.OrderService); }

	public get customer(): CustomerStore | undefined {
		return customersMainStore.getCustomerStore(this._model.customer_id);
	}

	public get orderId(): string { return this._model?.order_id ?? ''; }
	public get shipmentCustomerAddress(): string | undefined { return this._model?.shipment_customer_address; }
    public get hasCustomerEmail(): boolean {
        return Boolean(this._model?.has_customer_email ?? this._model?.customer_email);
    }
	public get orderName(): string { return this._model?.name ?? ''; }
	public get orderUrl(): string { return this._model?.orderUrl ?? ''; }
	public get totalProducts(): number { return this._model?.products.reduce((acc, product)=> acc + product.quantity, 0); }
	public get returnedProducts(): number { return this._model?.products.reduce((acc, product)=> acc + (product.status === 'returned' ? product.quantity : 0), 0) ?? 0; }
	public get removedProducts(): number { return this._model?.removedItemsInfo.quantity ?? 0; }
	public get rmaList() { return this._rmaList.list; }
    public get rmaCount(): number { return this._rmaList.total_count; }
	public get productList(): OrderProductStore[] { return this._productList.filter((product: OrderProductStore) => !product.isHidden); }
    public get paymentCurrencyCode(): string {return this._model?.payment_currency_code; }
    public get storeCurrencyCode(): string {return this._model?.store_currency_code; }
    /** conversion_rate is current convertion rate  */
    public get conversionRate(): number { return this._model?.conversion_rate; }
    public get locale(): string | undefined {
        return this._model?.locale ?? 'en';
    }
	public get salesChannel(): ISalesChannel | null { return this._model.sales_channel; }
	public get location(): ILocation | null { return this._model.location; }
	public get isCancelled(): boolean { return this._model?.isCancelled ?? false; }
    public get isRefunded(): boolean { return this._model?.isRefunded ?? false;}
	public get customerEmail(): string | undefined { return this._model?.customer_email; }
	public get isCreateRmaNotAllowed(): boolean { return this._eligibilityResponse?.create_rma_not_allowed ?? false; }
    public get parentOrderIds(): string[] { return this._model.parent_order_ids ?? []; }
    public get isCovered(): boolean { return this._model.is_covered ?? false; }
    public get glbeOrderId(): string | undefined { return this._model.glbe_order_id; }
    public get majorOrderId(): string | undefined { return this._model.major_order_id; }
    public get isMajorOrder(): boolean {
        return Boolean(this.glbeOrderId) && this.glbeOrderId === this.majorOrderId;
    }

	public async loadCustomer() {
		customersMainStore.fetchCustomersByIds([this._model?.customer_id]);
		this.customer?.loadRmasList();
	}

    public getShopifyOrderLink(): string{
        return `https://admin.shopify.com/store/${currentPortalStore.currentPortal.name?.split('.')[0]}/orders/${this._model.order_id}`;
    }

    /**
     * Get the latest fulfillment date from all products as UTC string.
     * @returns ISO UTC date string or undefined if no products are fulfilled
     */
    public getFulfilledDate(): string | undefined {
        return this.productList.slice().sort((a, b) => {
            if (!a.fulfillmentDate) {
                return -1;
            }
            else if (!b.fulfillmentDate) {
                return 1;
            }
            else {
                /** Use Date constructor only for comparison */
                return new Date(a.fulfillmentDate).getTime() > new Date(b.fulfillmentDate).getTime() ? -1 : 1;
            }
        }).pop()?.fulfillmentDate;
    }

    /**
     * Calculate days passed since fulfillment.
     * @returns Number of days or null if no fulfillment date
     */
    public getDaysSinceFulfillment(): number | null {
        const dateString = this.getFulfilledDate();
        if (!dateString) {
            return null;
        }
        /** Convert string to Date only for calculation */
        return calculateDaysPassed(new Date(dateString));
    }
    
    
    public getLineItemPolicies(lineItemId: string|null|undefined): number[]|undefined {
        return this._eligibilityResponse?.order_items?.find(i => i.line_item_id === lineItemId)?.available_policies;
    }
	
    public isLineItemIdOutOfPolicy(lineItemId: string): boolean {
        return this._eligibilityResponse?.order_items?.find(i => i.line_item_id === lineItemId)?.available_policies.length === 0;
    }

    public isLineItemReturnBlockedVirtual(lineItemId: string | null | undefined): boolean {
        return this._eligibilityResponse?.order_items?.find(i => i.line_item_id === lineItemId)?.is_return_blocked_virtual ?? false;
    }

    public isLineItemReturnBlockedDangerousGood(lineItemId: string | null | undefined): boolean {
        return this._eligibilityResponse?.order_items?.find(i => i.line_item_id === lineItemId)?.is_return_blocked_dangerous_good ?? false;
    }

    public isLineItemReturnBlockedBundleItem(lineItemId: string | null | undefined): boolean {
        return this._eligibilityResponse?.order_items?.find(i => i.line_item_id === lineItemId)?.is_return_blocked_bundle_item ?? false;
    }
    
    public isOrderOutOfPolicy(): boolean {
        return this._eligibilityResponse?.is_out_of_policy ?? false;
    }
    
	private _setProductStores(products: IProduct[]) {

		let groupedProducts: { [key: string]: IProduct[] } = {};

		products.forEach((product: IProduct) => {
			/*
				Group by, or in other words separate by these parameters:
			 */
			const mapKey = ( product.status === PRODUCT_STATUS_ENUM.RETURNED ? 'z_returned' : 'a') +  //product status (returned or not), a and z for sorting by not returned first, returned last
				product.fulfillment_status + //Fulfillment status (fulfilled or not
				product.shop_line_item_id + //line item id
				product.updatedAt + //fulfillment date
				product.presentment_money + //price actually paid
				product.rma?.id + // if was returned in our system (could be marked returned from shopify)
				(product.refund_info?.state ?? '') + //refunded outside ReturnGO (server-decided state)
				(product.refund_info?.effective_paid_amount ?? ''); //not-refunded remainder, differs per refunded unit

			if (!groupedProducts[mapKey]) {
				groupedProducts[mapKey] = [];
			}
			groupedProducts[mapKey].push(product);
		});
		
		groupedProducts = Object.keys(groupedProducts).sort().reduce(
			(obj: { [key: string]: IProduct[] }, key) => {
				obj[key] = groupedProducts[key];
				return obj;
			},
			{}
		);
		
		Object.values(groupedProducts).forEach((productList) => {
			this._productList.push(new OrderProductStore(this, productList));
		})
	}

    public get rmasList() { return this._rmasList; }
    public get isLoadingRmas() { return this._isLoadingRmas; }

    public async loadRmasList(){
        if (this._rmasList || this._isLoadingRmas || this.orderId === '') {
            /** do not request when in loading process or when loaded or when no order id */
            return;
        }

		try {
            this._isLoadingRmas = true;
            const rmaList = await this._orderService.fetchOrderRmasList(this.orderId);
            runInAction(() => {
                this._isLoadingRmas = false;
                this._rmasList = rmaList;
            })
        }
        catch (e) {
            runInAction(() => {
                this._isLoadingRmas = false;
            });
        }
	}
}
export default OrderStore;
